// src/components/ProductCard.js
import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import AddToCartSelectorDrawer from "./AddToCartSelectorDrawer";


function ProductCard({ product }) {
  const { lang } = useContext(LanguageContext);
  const { toggleWishlist, isInWishlist } = useCart();
  const navigate = useNavigate();

  const [selectorOpen, setSelectorOpen] = useState(false);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);

  // 🔥 Build size availability from DB
const allSizes = (product.sizes || []).map((s) => ({
  label: s.size,
}));


  const isMobile =
    typeof window !== "undefined" && window.innerWidth < 768;

  const inWishlist = isInWishlist(product.id);

  const mainImage =
    product.images?.[0] ||
    product.image ||
    "/images/placeholder-product.jpg";

  const displayName = lang === "EN" ? product.nameEN : product.nameAR;
  const hasDiscount = Number(product.discount) > 0;

  const availableSizes = new Set(
    (product.sizes || [])
      .map((s) => Number(s.size))
      .filter((v) => !Number.isNaN(v))
  );

  const hasOneSize = (product.sizes || []).some(
    (s) => String(s.size).toUpperCase() === "ONE"
  );

  const finalPrice = hasDiscount
    ? Math.round(product.price * (1 - product.discount / 100))
    : product.price;
const hasAvailableVariant =
  Array.isArray(product.sizes) && product.sizes.length > 0;

const stockInfo = {
  text: hasAvailableVariant
    ? lang === "EN" ? "IN STOCK" : "متوفر"
    : lang === "EN" ? "OUT OF STOCK" : "غير متوفر",
  cls: hasAvailableVariant ? "text-[#9EFFA1]" : "text-red-500",
};


  const handleOpenDetails = () =>
    navigate(`/product/${product.slug}`);

  const openSelector = (e) => {
    e.stopPropagation();
    setSelectorOpen(true);
  };

  const handleToggleWishlist = (e) => {
    e.stopPropagation();
    toggleWishlist({
  id: product.id,
  slug: product.slug,   // ✅ ADD THIS
  nameEN: product.nameEN,
  nameAR: product.nameAR,
  price: finalPrice,
  image: mainImage,
});
  };

  return (
    <>
      {/* CARD */}
      <div
        role="button"
        tabIndex={0}
        onClick={handleOpenDetails}
        className="
          group w-full bg-black
          border border-[#2E5A2A]
          overflow-hidden
          [content-visibility:auto]
          [contain-intrinsic-size:400px]
          transition-all
          hover:shadow-[0_0_25px_rgba(46,150,42,0.45)]
        "
      >
        {/* IMAGE */}
        <div className="relative overflow-hidden">
          <img
            src={mainImage}
            alt={displayName}
            loading="lazy"
            decoding="async"
            className={`
              w-full aspect-square object-cover
              ${!isMobile ? "transition-transform duration-500 group-hover:scale-110" : ""}
            `}
          />

          {/* DISCOUNT */}
          {hasDiscount && (
            <span className="
              absolute top-2 left-2
              bg-[#9EFFA1] text-black
              text-[11px] px-2 py-[3px]
              font-bold tracking-wide
            ">
              -{product.discount}%
            </span>
          )}

          {/* WISHLIST */}
          <button
            onClick={handleToggleWishlist}
            className="
              absolute top-2 right-2 w-8 h-8
              rounded-full
              bg-black/70 border border-[#2E5A2A]
              flex items-center justify-center
              hover:bg-[#2E5A2A]
              transition
            "
          >
            <span className={`text-lg ${inWishlist ? "text-red-500" : "text-[#9EFFA1]"}`}>
              {inWishlist ? "♥" : "♡"}
            </span>
          </button>
        </div>

        {/* DETAILS */}
        <div className="px-3 pt-3 pb-4">
          <p className="text-[13px] md:text-sm font-semibold text-white line-clamp-2">
            {displayName}
          </p>

          {product.public_id && (
            <p className="text-[11px] text-[#9EFFA1]/70 mt-[2px]">
              {product.public_id}
            </p>
          )}

          <p className={`text-[11px] mt-1 font-semibold ${stockInfo.cls}`}>
            {stockInfo.text}
          </p>

          <div className="mt-2 flex items-center gap-2">
            <span className="text-[13px] md:text-sm font-bold text-white">
              ${finalPrice}
            </span>
            {hasDiscount && (
              <span className="text-[11px] line-through text-white/40">
                ${product.price}
              </span>
            )}
          </div>

          {/* COLORS */}
          {product.colors?.length > 0 && (
            <div className="flex items-center gap-1 mt-2">
              {product.colors.slice(0, 4).map((c, idx) => (
                <span
                  key={idx}
                  className="w-3 h-3 rounded-full border border-[#2E5A2A]"
                  style={{ backgroundColor: c.code }}
                />
              ))}
            </div>
          )}

          {/* SIZE AVAILABILITY */}
          {/* SIZE AVAILABILITY */}
{allSizes.length > 0 && (
  <>
    {/* separator line */}
    <div className="w-full h-[1px] bg-white/10 my-2" />

    <div className="flex gap-1.5 flex-wrap">
      {allSizes.map((s) => (
  <div
    key={s.label}
    className="
      px-2 py-1 min-w-[22px]
      text-[11px] font-medium
      flex items-center justify-center
      border border-[#9EFFA1]
      text-[#9EFFA1]
    "
  >
    {s.label}
  </div>
))}

    </div>
  </>
)}


          {/* ADD TO CART */}
          <button
            onClick={openSelector}
            className="
              mt-3 w-full py-2
              bg-[#2E5A2A] text-white
              text-[12px] md:text-sm
              font-bold tracking-wide
              hover:bg-[#3D7A38]
              transition
            "
          >
            {lang === "EN" ? "ADD TO CART" : "أضيفي إلى السلة"}
          </button>
        </div>
      </div>

      {/* SELECTOR DRAWER */}
      {selectorOpen && (
        <AddToCartSelectorDrawer
          isOpen={selectorOpen}
          onClose={() => {
            setSelectorOpen(false);
            setSelectedSize(null);
            setSelectedColor(null);
          }}
          product={product}
          selectedSize={selectedSize}
          selectedColor={selectedColor}
          setSelectedSize={setSelectedSize}
          setSelectedColor={setSelectedColor}
        />
      )}
    </>
  );
}

export default ProductCard;
